import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { listMessages, sendMessage } from '../lib/api';
import { dateTime, friendlyError } from '../lib/format';
import type { Lead, Message } from '../lib/types';
import { Button, ErrorText } from './ui';

const statusLabel: Record<Message['status'], string> = {
  received: '', queued: 'Waiting to send', sent: 'Sent', delivered: 'Delivered', read: 'Read', failed: 'Not sent',
};

export function windowLeft(lastInbound: string | null | undefined, now = Date.now()): number {
  if (!lastInbound) return 0;
  return Math.max(0, new Date(lastInbound).getTime() + 24 * 3600e3 - now);
}

export function ChatPanel({ lead, nameOf, onSent }: { lead: Lead; nameOf: (id: string | null) => string; onSent: () => void }) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [lastInbound, setLastInbound] = useState(lead.buyer?.last_inbound_at ?? null);
  const end = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    const ms = await listMessages(lead.id).catch(() => null);
    if (!ms) return;
    setMessages(ms);
    const lastIn = [...ms].reverse().find((m) => m.direction === 'in');
    if (lastIn) setLastInbound((prev) => (!prev || lastIn.created_at > prev ? lastIn.created_at : prev));
  }, [lead.id]);

  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load]);
  useEffect(() => { end.current?.scrollIntoView({ block: 'nearest' }); }, [messages.length]);

  const left = windowLeft(lastInbound);
  const hours = Math.floor(left / 3600e3);
  const mins = Math.floor((left % 3600e3) / 60000);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true); setError('');
    try { await sendMessage(lead.id, text, lead.is_test); setText(''); await load(); onSent(); } catch (err) { setError(friendlyError(err)); } finally { setBusy(false); }
  }

  return (
    <section className="border-b border-line px-5 py-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted">WhatsApp</h3>
        <span className={`text-xs ${left ? 'text-ok' : 'text-warn'}`}>
          {left ? `Reply window: ${hours} h ${mins} min left` : messages.length ? 'Reply window closed' : 'No messages yet'}
        </span>
      </div>
      {messages.length > 0 && (
        <div className="mb-3 flex max-h-[320px] flex-col gap-2 overflow-y-auto rounded-xl bg-surface2 p-3">
          {messages.map((m) => (
            <div key={m.id} className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${m.direction === 'in' ? 'self-start bg-surface' : 'self-end bg-accentSoft'}`}>
              <p className="whitespace-pre-wrap">{m.body}</p>
              <p className={`mt-0.5 text-[11px] ${m.status === 'failed' ? 'text-bad' : 'text-muted'}`}>
                {m.direction === 'out' ? `${m.sender_kind === 'member' ? nameOf(m.sender_member_id) : 'AI desk'} · ` : ''}
                {dateTime(m.created_at)}{statusLabel[m.status] ? ` · ${statusLabel[m.status]}` : ''}{m.error ? ` · ${m.error}` : ''}
              </p>
            </div>
          ))}
          <div ref={end} />
        </div>
      )}
      {left > 0 ? (
        <form onSubmit={submit} className="flex gap-2">
          <input aria-label="Reply" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a reply"
            className="min-w-0 flex-1 rounded-lg border border-line px-3 py-2 text-sm outline-none focus:border-accent" />
          <Button type="submit" variant="primary" disabled={busy || !text.trim()}>{busy ? 'Sending…' : 'Send'}</Button>
        </form>
      ) : (
        <p className="text-sm text-muted">
          {messages.length
            ? "The buyer hasn't messaged in 24 hours, so only approved templates can be sent. Templates arrive in week 3; call the buyer for now."
            : 'When this buyer messages on WhatsApp, the chat appears here.'}
        </p>
      )}
      <div className="mt-2"><ErrorText>{error}</ErrorText></div>
    </section>
  );
}
